import { useEffect, useMemo, useState } from "react";
import { Button, ErrorNote, MoneyField, Panel } from "../../components/ui";
import { SaveBar } from "../../components/save";
import { formatMoney, parseMoney, centsToInput } from "../../lib/money";
import { supabase } from "../../lib/supabase";
import { mustSave, useSaveState } from "../../lib/saving";
import {
  buildListing, outTheDoor, withEquipment, FINANCE_FLOOR_CENTS,
  LISTING_BODY_TEMPLATE,
  type ListingVehicle, type ListingFees,
} from "../../lib/listing";
import { listVehicleDocs, shortShareLink, shareLink } from "./vehicleDocuments";
import { listPhotos, photosForDescription } from "./photos";
import { describeVehicle } from "../../lib/stripe";

/**
 * The text that goes on Facebook.
 *
 * The figures come from the same computeDeal the contract uses, so what is
 * advertised and what is signed cannot disagree. The description is yours to
 * edit — nothing here invents equipment or an owner count.
 */
export default function VehicleListing({ vehicleId, vehicle, onSaved }: {
  vehicleId: string;
  vehicle: ListingVehicle;
  /** What just landed in the database, so the money strip and gross above
      update without a reload — and so the page's copy of the description is
      the saved one. It used to keep the copy it loaded with, so opening and
      cancelling the edit form brought the old text back into this box as if
      the save had never happened. */
  onSaved: (saved: { listing_body: string; asking_price_cents?: number }) => void;
}) {
  /* One price, not two. This used to keep a separate advertised price, which
     meant the lot list, the gross and the reports could all disagree with
     what was actually posted on Facebook. The asking price is the price. */
  const [price, setPrice] = useState(() => centsToInput(vehicle.asking_price_cents));
  const [body, setBody] = useState(vehicle.listing_body ?? LISTING_BODY_TEMPLATE);
  const [fees, setFees] = useState<ListingFees | null>(null);
  const [carfax, setCarfax] = useState<string | null>(null);
  const [hasCarfax, setHasCarfax] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [wasReset, setWasReset] = useState(false);

  useEffect(() => {
    supabase.from("dealer_settings").select("*").maybeSingle().then(({ data }) => {
      if (!data) return;
      setFees({
        predeliveryServiceCents: data.default_predelivery_service_cents ?? 0,
        electronicFilingCents: data.default_electronic_filing_cents ?? 0,
        titleFeeCents: data.default_title_cents ?? 0,
        // A transfer is the title fee alone; a new plate adds registration.
        tagTransferCents: 0,
        newTagCents: data.default_license_reg_cents ?? 0,
        countySurtaxBps: 50,
      });
    });
  }, []);

  /* Only linked when a Carfax is actually attached. An advertised report
     nobody can open is worse than not mentioning one. */
  useEffect(() => {
    let live = true;
    listVehicleDocs(vehicleId)
      .then(async (docs) => {
        const cf = docs.find((d) => d.kind === "carfax");
        if (!live || !cf) return;
        setHasCarfax(true);
        try {
          setCarfax(await shortShareLink(cf));
        } catch {
          // No worker configured: the long signed URL still works.
          try { setCarfax(await shareLink(cf, 30)); } catch { /* optional */ }
        }
      })
      .catch(() => { /* the listing works without one */ });
    return () => { live = false; };
  }, [vehicleId]);

  const cents = parseMoney(price) ?? 0;
  const otd = useMemo(() => (fees ? outTheDoor(cents, fees) : null), [cents, fees]);
  const text = useMemo(
    () => (fees ? buildListing(vehicle, fees, cents, body, carfax) : ""),
    [vehicle, fees, cents, body, carfax]);

  /**
   * Draft the equipment list from the car's own photographs.
   *
   * The result is put beside the description rather than into it. It is a
   * draft from pictures, and a listing is an advertisement — you read it and
   * decide what is true before any of it goes in.
   */
  async function describe() {
    setDrafting(true); setErr(null); setDraft(null);
    try {
      const photos = await listPhotos(vehicleId);
      if (!photos.length) {
        setErr("Add some photos of the car first.");
        return;
      }
      const images = await photosForDescription(photos, 4);
      const { text } = await describeVehicle(images, {
        year: vehicle.year, make: vehicle.make,
        model: vehicle.model, trim: vehicle.trim_level,
      });
      setDraft(text);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not draft a description.");
    } finally {
      setDrafting(false);
    }
  }

  /**
   * Keep what was typed — when Save is pressed, and only then.
   *
   * This used to save itself 900ms after the typing stopped. That left no
   * moment at which the person could know the text had landed: a dropped
   * connection mid-autosave looked the same as a successful one until the
   * page was reloaded. The owner's rule is an explicit Save with a visible
   * "Saved ✓" — see CLAUDE.md — so that is what this is now. Leaving with
   * unsaved text asks first.
   */
  /* What is actually in the database, tracked here rather than read off the
     prop. The prop does not change when this panel saves, so comparing
     against it left the panel permanently dirty after one edit — and every
     navigation away warned about work that had already been saved. */
  const [savedBody, setSavedBody] = useState(vehicle.listing_body ?? LISTING_BODY_TEMPLATE);
  const [savedPrice, setSavedPrice] = useState(vehicle.asking_price_cents);

  /* An empty or unparseable box and a zero price are the same thing here.
     Comparing null against 0 left the panel dirty with nothing to save. */
  const dirty = body !== savedBody || (parseMoney(price) ?? 0) !== (savedPrice ?? 0);

  /* Unsaved changes, Saving…, Saved ✓ 3:04 PM, or Not saved with the reason.
     Also asks before the page is left while any of it is outstanding. */
  const saveState = useSaveState({ dirty, what: "this listing" });

  async function save() {
    /* What is sent is what gets marked saved — not whatever is in the box
       when the answer comes back, which may include typing done meanwhile. */
    const sentBody = body;
    const cents = parseMoney(price);
    /* Written even when nothing changed. The alternative is a button that
       does nothing on a screen where the person is specifically looking for
       proof their work is safe. */
    const patch: { listing_body: string; asking_price_cents?: number } = { listing_body: sentBody };
    if (cents !== null && cents !== savedPrice) {
      patch.asking_price_cents = cents;
    }
    await saveState.run(async () => {
      // .select so a write that matched no row (an expired session) is a
      // failure rather than a silent "saved".
      mustSave(await supabase
        .from("vehicles").update(patch).eq("id", vehicleId).select("id"));
      // Now it is what the database holds, so nothing is outstanding.
      setSavedBody(sentBody);
      if (patch.asking_price_cents !== undefined) setSavedPrice(patch.asking_price_cents);
      onSaved(patch);
    });
  }

  async function copy() {
    setBusy(true); setErr(null);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setErr("Could not copy. Select the text and copy it by hand.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Facebook listing" rail="money"
           note={otd ? `out the door ${formatMoney(otd.withOwnTagCents)}` : undefined}
           foot={
             <>
               <Button type="button" onClick={copy} disabled={busy || !fees}>
                 {copied ? "Copied" : "Copy listing"}
               </Button>
               <Button type="button" variant="ghost" onClick={describe} disabled={drafting || busy}>
                 {drafting ? "Reading the photos" : "Draft from photos"}
               </Button>
               {/* type="button" explicitly: a bare button inside any form
                   ancestor defaults to submit and does nothing visible. */}
               <Button type="button" variant="ghost"
                       onClick={() => {
                         setBody(LISTING_BODY_TEMPLATE);
                         setDraft(null);
                         setErr(null);
                         setWasReset(true);
                         setTimeout(() => setWasReset(false), 1800);
                       }}>
                 {wasReset ? "Description reset" : "Start the description over"}
               </Button>
             </>
           }>
      {!fees ? (
        <p className="hint">Loading your fee settings</p>
      ) : (
        <>
          <MoneyField label="Asking price" value={price} onChange={setPrice}
                      hint="Changing this changes the car's asking price everywhere" />

          {otd && cents > 0 && (
            <dl className="spec">
              <div><dt>Price</dt><dd>{formatMoney(otd.priceCents)}</dd></div>
              <div><dt>Sales tax</dt><dd>{formatMoney(otd.taxCents)}</dd></div>
              <div><dt>Out the door, own tag</dt>
                <dd>{formatMoney(otd.withOwnTagCents)}</dd></div>
              <div><dt>Out the door, new tag</dt>
                <dd>{formatMoney(otd.withNewTagCents)}</dd></div>
            </dl>
          )}

          {cents > 0 && cents < FINANCE_FLOOR_CENTS && (
            <p className="hint dim">
              Under {formatMoney(FINANCE_FLOOR_CENTS)}, so the Suncoast
              pre-approval line is left out — credit unions will not write a
              loan this small.
            </p>
          )}

          <p className="taxnote">
            Tax is worked out the way the contract works it out — 6% state plus
            the county surtax on the first $5,000 only. A flat 6.5% overstates
            it by $75 on a $20,000 car, and the buyer finds that out at the
            desk.
          </p>

          <div className="field">
            <label htmlFor="listing-body">Description</label>
            <textarea id="listing-body" className="listingbody" rows={16}
                      value={body} onChange={(e) => setBody(e.target.value)} />
            <p className="hint dim">
              Delete the lines that do not fit this car — the checklist is
              deliberately over-complete. Press &ldquo;Draft from photos&rdquo;
              to add what the camera can see underneath it. An advertised
              feature the car does not have is a problem with the buyer, not a
              typo.
            </p>
          </div>

          <div className="field">
            <label htmlFor="listing-out">Ready to paste</label>
            <textarea id="listing-out" className="listingbody out" rows={10}
                      readOnly value={text} />
            {!hasCarfax && (
              <p className="hint dim">
                No Carfax attached, so no link is included. Attach one above and
                it appears here.
              </p>
            )}
          </div>

          {draft && (
            <div className="field">
              <label>Drafted from the photos</label>
              <textarea className="listingbody" rows={10} readOnly value={draft} />
              <div className="actions">
                <Button variant="ghost"
                        onClick={() => { setBody((b) => withEquipment(b, draft)); setDraft(null); }}>
                  Use this list
                </Button>
                <Button variant="ghost" onClick={() => setDraft(null)}>Discard</Button>
              </div>
              <p className="hint dim">
                Only what was visible in the pictures. Heated seats, CarPlay,
                the number of keys and anything about history cannot be seen in
                a photograph, so they are not in here — add those yourself.
                Read it before you use it.
              </p>
            </div>
          )}

          {/* Never disabled except while a write is in flight. Pressing it
              when there is nothing to save just confirms the time, which is
              the reassurance the button exists to give. */}
          <SaveBar save={saveState} onSave={() => void save()}
                   label="Save listing" idle="No unsaved changes"
                   retryHint="Your text and price are still here — press Save listing to try again." />

          {err && <ErrorNote>{err}</ErrorNote>}
        </>
      )}
    </Panel>
  );
}
